import { parse, differenceInDays } from 'date-fns'

/**
 * Group orders per asset/coin into combined values to be used as columns
 * @param {Array} portfolioData the data retreived from the google spreadsheet
 * @param {Object} currentCoingeckoFeedMap mapping of the current price data from coingecko by gecko id
 * @returns {Object} { ethereum: {...}, cardano: {...} }
 */
export function getGroupedCoins(portfolioData, currentCoingeckoFeedMap){

    const ordersByGeckoId = getOrdersByGeckoIdMap(portfolioData)

    const totalPortfolioWorth = getTotalPortfolioWorth(ordersByGeckoId, currentCoingeckoFeedMap)

    const combinedOrdersByGeckoId = {} // { ethereum: {...}, cardano: {...} }
    
    for(const geckoId in ordersByGeckoId){
        if(!currentCoingeckoFeedMap[geckoId]){
            // this means we didn't find the asset in the coingecko api
            continue
        }
        const columnValues = formatOrdersToNeededColumnValues(ordersByGeckoId[geckoId], currentCoingeckoFeedMap[geckoId], totalPortfolioWorth)
        combinedOrdersByGeckoId[geckoId] = columnValues
    }

    return combinedOrdersByGeckoId
}

/**
 * Generates a mapping with an array of orders by gecko_id
 * Orders are sorted by date-time ASC as well
 * @param {Array} portfolioData the data retreived from the google spreadsheet
 * @returns {Object} { ethereum: [{...}], cardano: [{...}] }
 */
function getOrdersByGeckoIdMap(portfolioData){
    const ordersByGeckoId = {}

    for(const pfItem of portfolioData){
        if(!ordersByGeckoId[pfItem.gecko_id]){
            ordersByGeckoId[pfItem.gecko_id] = []
        }
        ordersByGeckoId[pfItem.gecko_id].push(pfItem)
    }
    
    // sort orders by date-time ASC
    for(const gecko_id in ordersByGeckoId){
        ordersByGeckoId[gecko_id] = sortOrdersByDateTime(ordersByGeckoId[gecko_id])
    }

    return ordersByGeckoId
}

/**
 * Sorts orders on date-time ASC
 * @param {Array} orders 
 * @returns {Array} sorted orders
 */
function sortOrdersByDateTime(orders){
    return orders.slice().sort((a, b) => {
        const dateA = parse(a.date, 'dd/MM/yyyy', new Date()).getTime()
        const dateB = parse(b.date, 'dd/MM/yyyy', new Date()).getTime()
        if(dateA == dateB){
            return (a.time || 0) - (b.time || 0)
        }
        return dateA - dateB
    })
}

/**
 * Combines the orders into values for the portfolio columns
 * To be used to combine multiple orders for the same asset
 * @param {Array} orders 
 * @param {Object} currentAssetGeckoData the current price and data from coingecko for the current asset
 * @param {Number} totalPfWorth total portfolio worth in USD
 * @returns {Object} column values
 */
function formatOrdersToNeededColumnValues(orders, currentAssetGeckoData, totalPfWorth){
    const {
        totalBoughtQuantity,
        totalSoldQuantity,
        totalBoughtFiat,
        totalSoldFiat,
        averagePaidForSingleCoin,
        earliestBuyDate
    } = getTransactionTypeRelatedCombinedValues(orders)
        
    const holdQuantity = totalBoughtQuantity - totalSoldQuantity
    const geckoId = orders[0].gecko_id
    const asset = orders[0].asset
    const totalInvested = totalBoughtFiat - totalSoldFiat
    const relativeBoughtFiat = averagePaidForSingleCoin * holdQuantity
    const daysSinceInitialBuy = differenceInDays(new Date(), earliestBuyDate)
    let name = ''
    let image = ''
    let change24h = 0
    let priceFiat = 0
    if(currentAssetGeckoData){ // not set means asset currently not holding
        name = currentAssetGeckoData.name
        image = currentAssetGeckoData.image.small
        change24h = currentAssetGeckoData.market_data.price_change_percentage_24h
        priceFiat = currentAssetGeckoData.market_data.current_price.usd
    }
    const currentWorth = holdQuantity * priceFiat
    const profitFiat = currentWorth - relativeBoughtFiat
    const totalProfitFiat = currentWorth + totalSoldFiat - totalBoughtFiat
    const profitPercentage = profitFiat / relativeBoughtFiat * 100
    const totalProfitPercentage = totalProfitFiat / totalBoughtFiat * 100
    const pfWeight = currentWorth / totalPfWorth * 100
    
    return {
        geckoId,
        asset,
        totalBoughtQuantity,
        totalSoldQuantity,
        totalBoughtFiat,
        totalSoldFiat,
        totalInvested,
        relativeBoughtFiat,
        holdQuantity,
        averagePaidForSingleCoin,
        earliestBuyDate,
        daysSinceInitialBuy,
        name,
        image,
        change24h,
        priceFiat,
        currentWorth,
        profitFiat,
        totalProfitFiat,
        profitPercentage,
        totalProfitPercentage,
        pfWeight,
        totalPfWorth
    }
}

/**
 * Combines the orders into totals related to the transaction type
 * @param {Array} orders 
 * @returns {Object} combined fields
 */
export function getTransactionTypeRelatedCombinedValues(orders){

    let totalBoughtQuantity = 0
    let totalSoldQuantity = 0
    let totalBoughtFiat = 0
    let totalSoldFiat = 0
    let averagePaidForSingleCoin = 0
    let earliestBuyDate = null

    for(const order of orders){
        
        const orderTotalPrice = parseFloat(order.total_price.replace(',','.'))
        
        if(order.transaction_type.toUpperCase() === 'BUY'){

            const currentHoldingQuantity = Math.max(totalBoughtQuantity - totalSoldQuantity, 0)
            averagePaidForSingleCoin = (averagePaidForSingleCoin * currentHoldingQuantity + orderTotalPrice) / (currentHoldingQuantity + parseFloat(order.quantity))

            totalBoughtQuantity += parseFloat(order.quantity.replace(',','.'))
            totalBoughtFiat += orderTotalPrice
            if(earliestBuyDate == null){
                // the orders are already sorted
                earliestBuyDate = parse(order.date, 'dd/MM/yyyy', new Date())
            }

        }else{
            totalSoldQuantity += parseFloat(order.quantity.replace(',','.'))
            totalSoldFiat += orderTotalPrice

            if(totalBoughtQuantity <= totalSoldQuantity){
                // reset earliestBuyDate because at this point we're not holding this asset anymore
                earliestBuyDate = null
            }
        }

    }

    return { totalBoughtQuantity, totalSoldQuantity, totalBoughtFiat, totalSoldFiat, averagePaidForSingleCoin, earliestBuyDate }
}

/**
 * Calculates the total portfolio worth in USD
 * @param {Object} ordersByGeckoId { ethereum: [{...}], cardano: [{...}] }
 * @param {Object} currentCoingeckoFeedMap mapping of the current price data from coingecko by gecko id
 * @returns {Number} total portfolio worth in USD
 */
function getTotalPortfolioWorth(ordersByGeckoId, currentCoingeckoFeedMap){

    let totalPfWorth = 0

    for(const geckoId in ordersByGeckoId){
        const { totalBoughtQuantity, totalSoldQuantity } = getTransactionTypeRelatedCombinedValues(ordersByGeckoId[geckoId])

        const holdQuantity = totalBoughtQuantity - totalSoldQuantity
        if(holdQuantity <= 0){
            continue
        }
        if(!currentCoingeckoFeedMap[geckoId]){
            // this means we didn't find the asset in the coingecko api
            continue
        }

        totalPfWorth += holdQuantity * currentCoingeckoFeedMap[geckoId].market_data.current_price.usd
    }

    return totalPfWorth
}

/**
 * Retreives the currently holding assets gecko_ids
 * @param {Array} portfolioData the data retreived from the google spreadsheet
 */
export function getHoldingAssetsGeckoIds(portfolioData){

    const ordersByGeckoId = getOrdersByGeckoIdMap(portfolioData)

    const geckoIds = []

    for(const geckoId in ordersByGeckoId){
        const { totalBoughtQuantity, totalSoldQuantity } = getTransactionTypeRelatedCombinedValues(ordersByGeckoId[geckoId])

        const holdQuantity = totalBoughtQuantity - totalSoldQuantity
        if(holdQuantity <= 0){
            continue
        }

        geckoIds.push(geckoId)
    }

    return geckoIds
}